import React, { useState, useMemo } from 'react';
import { Tabs, Button } from 'antd';
import moment from 'moment';
import {
  LineChartOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  FileOutlined,
  ProjectOutlined,
} from '@ant-design/icons';

import AnalyticsDateFilter from './AnalyticsDateFilter';
import AnalyticsFilters from './AnalyticsFilters';
import AnalyticsExport from './AnalyticsExport';
import { OverviewTab, BreakdownTab } from './tabs';

import useAnalyticsDateRange from '../../hooks/useAnalyticsDateRange';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import useResponsiveTask from '../../hooks/useResponsiveTask';

import { space, radius, shadow } from '../../configs/designTokens';

const { TabPane } = Tabs;

const EMPTY_FILTERS = {};
const EMPTY_SLA = {};

const INITIAL_FILTERS = {
  assignees: [],
  requestors: [],
  requestTypes: [],
  statuses: [],
  urgencies: [],
  advertiserTypes: [],
  channelPartners: [],
  advertisers: [],
  teams: [],
};

const applyFilters = (tasks, filters) => {
  let result = tasks || [];

  if (filters.assignees?.length) {
    result = result.filter((t) => {
      const ids = [];
      if (t.assigneeUsers?.length) ids.push(...t.assigneeUsers.map((u) => u.id));
      if (t.assigneeUser?.id) ids.push(t.assigneeUser.id);
      return ids.some((id) => filters.assignees.includes(id));
    });
  }
  if (filters.requestors?.length) {
    result = result.filter((t) => filters.requestors.includes(t.createdBy?.id));
  }
  if (filters.requestTypes?.length) {
    result = result.filter((t) => filters.requestTypes.includes(t.requestType));
  }
  if (filters.statuses?.length) {
    result = result.filter((t) => filters.statuses.includes(t.status));
  }
  if (filters.urgencies?.length) {
    result = result.filter((t) => filters.urgencies.includes(t.urgency));
  }
  if (filters.advertiserTypes?.length) {
    result = result.filter((t) =>
      filters.advertiserTypes.includes(t.createdBy?.roles?.[0]?.org?.level)
    );
  }
  if (filters.channelPartners?.length) {
    result = result.filter((t) =>
      filters.channelPartners.includes(t.channelPartner?.name)
    );
  }
  if (filters.advertisers?.length) {
    result = result.filter((t) => filters.advertisers.includes(t.advertiser?.name));
  }
  if (filters.teams?.length) {
    result = result.filter((t) =>
      t.teams?.some((team) => filters.teams.includes(team.id))
    );
  }

  return result;
};

const TaskAnalytics = () => {
  const { isMobile } = useResponsiveTask();

  const [activeTab, setActiveTab] = useState('overview');
  const [projectSegment, setProjectSegment] = useState('all');
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  // Date range management
  const {
    dateRange,
    rangeKey,
    handlePresetChange,
    handleCustomRange,
  } = useAnalyticsDateRange();

  // Analytics data + derived filter options (no breakdown filters at query level)
  const { metrics, loading, rawTasks, filterOptions } = useAnalyticsData(dateRange, EMPTY_FILTERS, EMPTY_SLA, projectSegment);

  // Apply global filters client-side
  const filteredTasks = useMemo(() => applyFilters(rawTasks, filters), [rawTasks, filters]);

  // Recompute metrics + trend from filtered tasks
  const filteredMetrics = useMemo(() => {
    const total = filteredTasks.length;
    const completed = filteredTasks.filter((t) => t.status === 'COMPLETED').length;
    const open = total - completed;

    // Rebuild trend from filtered tasks
    const dateMap = {};
    if (dateRange && dateRange[0] && dateRange[1]) {
      let start = moment(dateRange[0]).startOf('day');
      const end = moment(dateRange[1]).startOf('day');

      if (filteredTasks.length > 0) {
        const earliest = filteredTasks.reduce((min, t) => {
          const d = moment(t.createdAt);
          return d.isBefore(min) ? d : min;
        }, moment(filteredTasks[0].createdAt));
        const earliestDay = earliest.startOf('day');
        if (earliestDay.isAfter(start)) {
          start = earliestDay;
        }
      }

      const current = start.clone();
      while (current.isSameOrBefore(end)) {
        const key = current.format('YYYY-MM-DD');
        dateMap[key] = { date: key, created: 0 };
        current.add(1, 'day');
      }
    }
    filteredTasks.forEach((task) => {
      const createdDate = moment(task.createdAt).format('YYYY-MM-DD');
      if (dateMap[createdDate]) {
        dateMap[createdDate].created += 1;
      }
    });
    const trend = Object.values(dateMap);

    return { ...metrics, total, completed, open, trend };
  }, [filteredTasks, metrics, dateRange]);

  return (
    <div
      className="admin-analytics"
      style={{
        marginTop: space[2],
        background: '#fff',
        borderRadius: radius.lg,
        padding: isMobile ? space[3] : space[4],
        boxShadow: shadow.xs,
      }}
    >
      {/* Date Range + Segment + Export */}
      <div className="task-filters__container" style={{ marginBottom: space[4] }}>
        <div className="task-filters__primary">
          <div className="task-filters__primary-row" style={{ justifyContent: 'space-between' }}>
            <AnalyticsDateFilter
              dateRange={dateRange}
              rangeKey={rangeKey}
              onPresetChange={handlePresetChange}
              onCustomRange={handleCustomRange}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                className="task-filter-select-btn"
                type={projectSegment === 'all' ? 'primary' : 'default'}
                onClick={() => setProjectSegment('all')}
                size={isMobile ? 'small' : 'middle'}
              >
                <AppstoreOutlined /> All Tasks
              </Button>
              <Button
                className="task-filter-select-btn"
                type={projectSegment === 'noProject' ? 'primary' : 'default'}
                onClick={() => setProjectSegment('noProject')}
                size={isMobile ? 'small' : 'middle'}
              >
                <FileOutlined /> General
              </Button>
              <Button
                className="task-filter-select-btn"
                type={projectSegment === 'projectOnly' ? 'primary' : 'default'}
                onClick={() => setProjectSegment('projectOnly')}
                size={isMobile ? 'small' : 'middle'}
              >
                <ProjectOutlined /> Project
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Global Filters */}
      <AnalyticsFilters
        filters={filters}
        onFiltersChange={setFilters}
        assigneeOptions={filterOptions.assignees}
        requestorOptions={filterOptions.requestors}
        channelPartnerOptions={filterOptions.channelPartners}
        advertiserOptions={filterOptions.advertisers}
        teamOptions={filterOptions.teams}
      />

      {/* Tabbed Content */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size={isMobile ? 'small' : 'default'}
        destroyInactiveTabPane
        tabBarExtraContent={<AnalyticsExport rawTasks={filteredTasks} rangeKey={rangeKey} />}
      >
        <TabPane
          tab={<span><LineChartOutlined /> Overview</span>}
          key="overview"
        >
          <OverviewTab metrics={filteredMetrics} loading={loading} />
        </TabPane>

        <TabPane
          tab={<span><BarChartOutlined /> Breakdown</span>}
          key="breakdown"
        >
          <BreakdownTab
            filteredTasks={filteredTasks}
            loading={loading}
            rangeKey={rangeKey}
            assigneeFilter={filters.assignees}
          />
        </TabPane>

      </Tabs>
    </div>
  );
};

export default TaskAnalytics;
